"use client"

import { useEffect, useState } from "react"
import { X } from "lucide-react"

const font = { fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif" } as const

export interface Toast {
  id: string
  message: string
  variant?: "default" | "error"
  action?: {
    label: string
    onClick: () => void
  }
  duration?: number
  /** 0–100. When set, renders a progress bar instead of auto-dismissing by timer. */
  progress?: number
}

interface ToastProps {
  toast: Toast
  onDismiss: (id: string) => void
}

function ToastItem({ toast, onDismiss }: ToastProps) {
  const [visible, setVisible] = useState(false)
  const hasProgress = toast.progress !== undefined
  const progress = toast.progress ?? 0
  const isComplete = hasProgress && progress >= 100
  const isError = toast.variant === "error"

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true))

    if (isError) return

    // Progress toasts stay pinned while in flight. Once complete (100%), they
    // linger 3s so the user sees the green "Installed" state, then dismiss.
    if (hasProgress && progress < 100) return

    const timer = setTimeout(
      () => {
        setVisible(false)
        setTimeout(() => onDismiss(toast.id), 200)
      },
      hasProgress ? 3000 : (toast.duration ?? 5000)
    )

    return () => clearTimeout(timer)
  }, [toast, onDismiss, hasProgress, progress, isError])

  return (
    <div
      style={{
        ...font,
        minWidth: 280,
        maxWidth: 380,
        background: isError
          ? "linear-gradient(180deg, rgba(32,10,14,0.96), rgba(12,12,16,0.96))"
          : "var(--glass-bg)",
        backdropFilter: "blur(20px)",
        border: isError
          ? "1px solid rgba(248,113,113,0.42)"
          : "1px solid var(--color-border)",
        borderRadius: 10,
        boxShadow: "var(--shadow-elevated, 0 8px 24px rgba(0,0,0,0.4))",
        overflow: "hidden",
        transition: "transform 0.2s ease, opacity 0.2s ease",
        transform: visible ? "translateX(0)" : "translateX(-12px)",
        opacity: visible ? 1 : 0,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "10px 12px",
        }}
      >
        <p
          style={{
            flex: 1,
            margin: 0,
            fontSize: 12,
            color: isError ? "#fecaca" : "var(--color-text-primary, white)",
            lineHeight: 1.4,
          }}
        >
          {toast.message}
        </p>
        {toast.action && (
          <button
            onClick={toast.action.onClick}
            style={{
              ...font,
              flexShrink: 0,
              fontSize: 11,
              fontWeight: 500,
              padding: "3px 10px",
              borderRadius: 6,
              border: "1px solid rgba(59,130,246,0.4)",
              background: "rgba(59,130,246,0.15)",
              color: "#60A5FA",
              cursor: "pointer",
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) => {
              ;(e.currentTarget as HTMLElement).style.background =
                "rgba(59,130,246,0.25)"
            }}
            onMouseLeave={(e) => {
              ;(e.currentTarget as HTMLElement).style.background =
                "rgba(59,130,246,0.15)"
            }}
          >
            {toast.action.label}
          </button>
        )}
        <button
          onClick={() => {
            setVisible(false)
            setTimeout(() => onDismiss(toast.id), 200)
          }}
          style={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 20,
            height: 20,
            padding: 0,
            border: "none",
            background: "transparent",
            color: "var(--color-text-tertiary)",
            cursor: "pointer",
            borderRadius: 4,
            transition: "color 0.15s, background 0.15s",
          }}
          onMouseEnter={(e) => {
            ;(e.currentTarget as HTMLElement).style.color =
              "var(--color-text-primary, white)"
            ;(e.currentTarget as HTMLElement).style.background =
              "rgba(255,255,255,0.06)"
          }}
          onMouseLeave={(e) => {
            ;(e.currentTarget as HTMLElement).style.color =
              "var(--color-text-tertiary)"
            ;(e.currentTarget as HTMLElement).style.background = "transparent"
          }}
        >
          <X className="h-3 w-3" />
        </button>
      </div>

      {hasProgress && (
        <div
          style={{
            height: 2,
            width: "100%",
            background: "rgba(255,255,255,0.06)",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${Math.min(progress, 100)}%`,
              background: isError ? "#f87171" : isComplete ? "#4ADE80" : "#60A5FA",
              transition: "width 0.3s ease-out, background 0.2s",
            }}
          />
        </div>
      )}
    </div>
  )
}

interface ToastContainerProps {
  toasts: Toast[]
  onDismiss: (id: string) => void
}

export function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  if (!toasts || toasts.length === 0) return null

  return (
    <div
      style={{
        position: "fixed",
        left: 16,
        bottom: 16,
        zIndex: 100,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  )
}
